// ===== Profile Edit Modal =====
// Phase 4: roster '나' 행 클릭 → 내 이름/사진 편집.
// confirm-modal.js와 동일한 plugin-restart-modal-* CSS 클래스를 재사용한다(새 모달 프레임워크 금지).
// 사진은 새 업로드 라우트를 만들지 않고 기존 POST /api/upload를 그대로 재사용한다.

const ProfileEdit = (() => {
  interface ProfileActor { displayName?: string; avatarUrl?: string | null }
  interface SaveFields { nameInput: HTMLInputElement; fileInput: HTMLInputElement }
  function _isRecord(value: unknown): value is Record<string, unknown> { return !!value && typeof value === "object" && !Array.isArray(value); }
  function _showError(errorEl: HTMLElement, message: string) {
    errorEl.textContent = message;
    errorEl.style.display = "";
  }

  async function _uploadPicture(file: File) {
    if (typeof currentSessionId !== "string" || !currentSessionId) throw new Error("session_required");
    const formData = new FormData();
    formData.append("sessionId", currentSessionId);
    formData.append("files", file);
    const res = await fetch("/api/upload", { method: "POST", body: formData });
    const data: unknown = await res.json().catch(() => null);
    if (!res.ok || !_isRecord(data) || data.ok !== true || !Array.isArray(data.files) || !_isRecord(data.files[0])
      || typeof data.files[0].id !== "string" || typeof data.files[0].path !== "string"
      || data.files[0].path !== `/api/files/${encodeURIComponent(data.files[0].id)}/content`) {
      throw new Error("upload_failed");
    }
    return data.files[0].path;
  }

  async function _save({ nameInput, fileInput }: SaveFields) {
    const payload: { displayName?: string; pictureUrl?: string; sessionId?: string } = {};

    const trimmedName = nameInput.value.trim();
    if (trimmedName) payload.displayName = trimmedName;

    const file = fileInput.files?.[0];
    if (file) {
      const pictureSessionId = currentSessionId;
      if (typeof pictureSessionId !== "string" || !pictureSessionId) throw new Error("session_required");
      payload.pictureUrl = await _uploadPicture(file);
      payload.sessionId = pictureSessionId;
    }

    const res = await fetch("/api/rooms/me", {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data: unknown = await res.json().catch(() => null);
    if (!res.ok || !_isRecord(data) || data.ok !== true) {
      throw new Error(_isRecord(data) && typeof data.error === "string" ? data.error : "profile_update_failed");
    }

    // 영속: 이름/사진의 실제 소스는 operational_users(DB) — 로스터는 항상
    // /api/rooms/actors를 다시 조회하므로 refresh 한 번으로 즉시 반영된다(CAP12 치트 방지).
    if (typeof RosterList !== "undefined") RosterList.refresh();
    if (window.BigtionAuth?.checkAuth) window.BigtionAuth.checkAuth();
  }

  function open(actor: ProfileActor = {}) {
    const overlay = document.createElement("div");
    overlay.className = "plugin-restart-modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "profileEditModalTitle");

    const modal = document.createElement("div");
    modal.className = "plugin-restart-modal";

    const header = document.createElement("div");
    header.className = "plugin-restart-modal-header";
    header.id = "profileEditModalTitle";
    header.textContent = "내 프로필 편집";

    const body = document.createElement("div");
    body.className = "plugin-restart-modal-body";

    const nameLabel = document.createElement("p");
    nameLabel.textContent = "이름";
    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.value = actor.displayName || "";

    const fileLabel = document.createElement("p");
    fileLabel.textContent = "사진 (선택)";

    // 사진 편집: 아바타 미리보기 + 테마 파일 픽커(네이티브 input은 숨기고 라벨 버튼으로 감싼다).
    // 네이티브 "Choose File" 이 테마와 어긋나던 문제를 닫는다. fileInput 요소 자체는 유지해
    // _save 가 fileInput.files[0] 를 그대로 읽는다.
    const photoRow = document.createElement("div");
    photoRow.className = "plugin-restart-photo-row";

    const avatar = document.createElement("span");
    avatar.className = "plugin-restart-avatar";
    avatar.textContent = (actor.displayName || "").trim().charAt(0) || "?";
    if (actor.avatarUrl) {
      const img = document.createElement("img");
      img.className = "plugin-restart-avatar-img";
      img.src = actor.avatarUrl;
      img.alt = "";
      img.referrerPolicy = "no-referrer";
      img.addEventListener("error", () => img.remove());
      avatar.appendChild(img);
    }

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";

    const pickBtn = document.createElement("label");
    pickBtn.className = "plugin-restart-file-btn";
    pickBtn.textContent = "사진 선택";
    pickBtn.appendChild(fileInput);

    const fileName = document.createElement("span");
    fileName.className = "plugin-restart-file-name";
    fileName.textContent = "선택된 파일 없음";

    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      fileName.textContent = file ? file.name : "선택된 파일 없음";
      if (file) {
        let img = avatar.querySelector("img");
        if (!img) {
          img = document.createElement("img");
          img.className = "plugin-restart-avatar-img";
          img.alt = "";
          avatar.appendChild(img);
        }
        img.src = URL.createObjectURL(file);
      }
    });

    const pick = document.createElement("div");
    pick.className = "plugin-restart-file-pick";
    pick.appendChild(pickBtn);
    pick.appendChild(fileName);

    photoRow.appendChild(avatar);
    photoRow.appendChild(pick);

    const errorP = document.createElement("p");
    errorP.className = "plugin-restart-warning";
    errorP.style.display = "none";

    body.appendChild(nameLabel);
    body.appendChild(nameInput);
    body.appendChild(fileLabel);
    body.appendChild(photoRow);
    body.appendChild(errorP);

    const footer = document.createElement("div");
    footer.className = "plugin-restart-modal-footer";

    const cancelBtn = document.createElement("button");
    cancelBtn.className = "agent-btn agent-btn-cancel";
    cancelBtn.textContent = "취소";

    const saveBtn = document.createElement("button");
    saveBtn.className = "agent-btn agent-btn-save";
    saveBtn.textContent = "저장";

    footer.appendChild(cancelBtn);
    footer.appendChild(saveBtn);
    modal.appendChild(header);
    modal.appendChild(body);
    modal.appendChild(footer);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    OverlayRegistry.register("profile-edit", close);

    function close() {
      overlay.remove();
      document.removeEventListener("keydown", onEscape);
      OverlayRegistry.unregister("profile-edit");
    }

    function onEscape(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }

    cancelBtn.addEventListener("click", close);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close();
    });
    document.addEventListener("keydown", onEscape);

    saveBtn.addEventListener("click", async () => {
      errorP.style.display = "none";
      try {
        await withLoadingFeedback(saveBtn, () => _save({ nameInput, fileInput }), {
          loadingText: "저장 중...",
        });
        close();
      } catch (err) {
        console.error("[profile-edit] 저장 실패:", err);
        _showError(errorP, "저장에 실패했어요. 다시 시도해 주세요.");
      }
    });

    nameInput.focus();
  }

  return { open };
})();
